/*
 * Tabbed content panel — Figma "Frame 43" on the product page.
 *
 * The markup ships with every panel present and all but the first carrying
 * `hidden`, so the content is in the DOM for crawlers and for a reader that
 * never runs this file; the element only takes over the showing and hiding.
 *
 * Keyboard behaviour follows the WAI-ARIA tabs pattern: arrows move between
 * tabs (wrapping at each end), Home/End jump to the first/last, and only the
 * selected tab is in the tab order, so Tab moves out of the tablist and into
 * the panel rather than walking through every tab.
 */
if (!customElements.get('product-tabs')) {
  class ProductTabs extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
      if (this.tabs.length === 0) return;

      this.tabs.forEach((tab) => {
        tab.addEventListener('click', () => this.select(tab));
        tab.addEventListener('keydown', (event) => this.onKeyDown(event));
      });
    }

    panelFor(tab) {
      const id = tab.getAttribute('aria-controls');
      return id ? this.querySelector(`#${CSS.escape(id)}`) : null;
    }

    select(tab, { focus = false } = {}) {
      this.tabs.forEach((candidate) => {
        const isSelected = candidate === tab;

        candidate.setAttribute('aria-selected', isSelected ? 'true' : 'false');
        // Roving tabindex: the tablist is one stop, not one per tab.
        candidate.setAttribute('tabindex', isSelected ? '0' : '-1');
        this.panelFor(candidate)?.toggleAttribute('hidden', !isSelected);
      });

      if (focus) tab.focus();
    }

    onKeyDown(event) {
      const index = this.tabs.indexOf(event.currentTarget);
      if (index === -1) return;

      let next;
      switch (event.key) {
        case 'ArrowRight':
        case 'ArrowDown':
          next = (index + 1) % this.tabs.length;
          break;
        case 'ArrowLeft':
        case 'ArrowUp':
          next = (index - 1 + this.tabs.length) % this.tabs.length;
          break;
        case 'Home':
          next = 0;
          break;
        case 'End':
          next = this.tabs.length - 1;
          break;
        default:
          return;
      }

      event.preventDefault();
      this.select(this.tabs[next], { focus: true });
    }
  }

  customElements.define('product-tabs', ProductTabs);
}

/*
 * Theme editor support.
 *
 * Without this, clicking "Tab — Technical Information" in the editor sidebar
 * highlights the tab button but leaves the Features panel on screen, so the
 * merchant edits one tab's rich text while looking at another tab's content and
 * concludes the tabs cannot be edited. Shopify fires `shopify:block:select` at
 * the element carrying `block.shopify_attributes` — the tab button — so
 * opening the matching panel is all that is needed.
 *
 * Registered once at document level rather than per element, because the
 * section is re-rendered on every settings change and per-element listeners
 * would be thrown away with it.
 */
if (!window.__trueRestProductTabsEditor) {
  window.__trueRestProductTabsEditor = true;

  document.addEventListener('shopify:block:select', (event) => {
    const tab = event.target.closest('[role="tab"]');
    if (!tab) return;

    const host = tab.closest('product-tabs');
    // `tabs` is assigned in connectedCallback; a selection that arrives before
    // the element has upgraded is safe to ignore, because the freshly rendered
    // section opens on its first tab anyway.
    if (!host || !host.tabs || host.tabs.length === 0) return;

    host.select(tab);
    host.scrollIntoView({ behavior: 'smooth', block: 'center' });
  });
}
